import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, LayoutDashboard, FileText, Mic, Wrench, Inbox, Save, Plus, Trash2, Users, Calendar } from "lucide-react";
import MarkdownEditor from "@/components/MarkdownEditor";
import ImageUploader from "@/components/ImageUploader";

const TABS = [
  { id: "content", label: "Site Content", icon: LayoutDashboard },
  { id: "services", label: "Services", icon: Wrench },
  { id: "talks", label: "Speaking", icon: Mic },
  { id: "posts", label: "Insights", icon: FileText },
  { id: "messages", label: "Messages", icon: Inbox },
  { id: "bookings", label: "Bookings", icon: Calendar },
  { id: "subscribers", label: "Subscribers", icon: Users },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { refresh } = useContent();
  const [tab, setTab] = useState("content");

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-10">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">[ cms · admin ]</div>
          <h1 className="font-serif text-3xl tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">Signed in as {user?.email}</p>
        </div>
        <button
          onClick={logout}
          data-testid="admin-logout-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border font-mono text-xs uppercase tracking-[0.2em] hover:bg-secondary transition-colors"
        >
          <LogOut className="w-3.5 h-3.5" /> Sign out
        </button>
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-3">
          <nav className="space-y-1 sticky top-24">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`admin-tab-${t.id}`}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-left transition-colors ${
                    tab === t.id ? "bg-secondary text-foreground" : "text-muted-foreground hover:bg-secondary/50"
                  }`}
                >
                  <Icon className="w-4 h-4" /> {t.label}
                </button>
              );
            })}
          </nav>
        </aside>
        <div className="lg:col-span-9">
          {tab === "content" && <ContentEditor onSaved={refresh} />}
          {tab === "services" && <CollectionEditor kind="services" onSaved={refresh} />}
          {tab === "talks" && <CollectionEditor kind="talks" onSaved={refresh} />}
          {tab === "posts" && <PostsEditor onSaved={refresh} />}
          {tab === "messages" && <SimpleList kind="messages" title="Inbox" desc="Contact form submissions." renderItem={(m) => (
            <>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="font-serif text-lg">{m.subject}</div>
                  <div className="text-xs text-muted-foreground">{m.name} · <a className="link-underline" href={`mailto:${m.email}`}>{m.email}</a></div>
                </div>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{new Date(m.created_at).toLocaleString()}</span>
              </div>
              <p className="text-sm whitespace-pre-wrap leading-relaxed">{m.message}</p>
            </>
          )} />}
          {tab === "bookings" && <SimpleList kind="bookings" title="Speaking bookings" desc="Requests from /speaking/book." renderItem={(b) => (
            <>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="font-serif text-lg">[SPEAKER REQUEST] {b.topic} — {b.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {b.organization || "—"} · <a className="link-underline" href={`mailto:${b.email}`}>{b.email}</a>
                  </div>
                </div>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{new Date(b.created_at).toLocaleString()}</span>
              </div>
              <div className="grid sm:grid-cols-4 gap-3 text-xs mt-3">
                <Meta label="Date" value={b.event_date} />
                <Meta label="Audience" value={b.audience_size} />
                <Meta label="Location" value={b.location || "—"} />
                <Meta label="Budget" value={b.budget || "—"} />
              </div>
              {b.message && <p className="mt-4 text-sm whitespace-pre-wrap leading-relaxed">{b.message}</p>}
            </>
          )} />}
          {tab === "subscribers" && <SubscribersView />}
        </div>
      </div>
    </div>
  );
}

// ------------------------- CONTENT (single doc) -------------------------
const CONTENT_FIELDS = [
  { k: "hero_name", label: "Hero Name", type: "input" },
  { k: "hero_tagline", label: "Hero Tagline", type: "input" },
  { k: "hero_subtitle", label: "Hero Subtitle", type: "textarea" },
  { k: "hero_cta_primary", label: "Hero CTA (Primary)", type: "input" },
  { k: "hero_cta_secondary", label: "Hero CTA (Secondary)", type: "input" },
  { k: "about_headline", label: "About Headline", type: "textarea" },
  { k: "about_body", label: "About — The Work", type: "textarea" },
  { k: "about_ministry", label: "About — The Ministry", type: "textarea" },
  { k: "about_transition", label: "About — The Transition", type: "textarea" },
  { k: "about_current_role", label: "Currently — Role title", type: "input" },
  { k: "about_current_company", label: "Currently — Company / dates", type: "input" },
  { k: "contact_blurb", label: "Contact Blurb", type: "textarea" },
  { k: "contact_email", label: "Contact Email", type: "input" },
  { k: "contact_linkedin", label: "LinkedIn URL", type: "input" },
  { k: "contact_github", label: "GitHub URL", type: "input" },
  { k: "contact_location", label: "Location", type: "input" },
];

function ContentEditor({ onSaved }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/content");
      setData(data.content);
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", data);
      toast.success("Site content updated.");
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <div className="font-mono text-xs text-muted-foreground">[ loading ]</div>;

  return (
    <div className="space-y-5" data-testid="content-editor">
      <SectionHeader title="Site Content" desc="Edit hero, about, and contact copy." />
      {CONTENT_FIELDS.map((f) => (
        <FieldRow key={f.k} label={f.label}>
          {f.type === "textarea" ? (
            <textarea
              rows={3}
              value={data[f.k] || ""}
              onChange={(e) => setData({ ...data, [f.k]: e.target.value })}
              data-testid={`content-${f.k}`}
              className="w-full bg-transparent border border-border rounded-lg px-4 py-3 outline-none focus:border-foreground transition-colors resize-none"
            />
          ) : (
            <input
              value={data[f.k] || ""}
              onChange={(e) => setData({ ...data, [f.k]: e.target.value })}
              data-testid={`content-${f.k}`}
              className="w-full bg-transparent border border-border rounded-lg px-4 py-2.5 outline-none focus:border-foreground transition-colors"
            />
          )}
        </FieldRow>
      ))}

      <FieldRow label="About — Certifications (one per line)">
        <textarea
          rows={8}
          value={(data.about_certifications || []).join("\n")}
          onChange={(e) => setData({ ...data, about_certifications: e.target.value.split("\n").map((l) => l.trim()).filter(Boolean) })}
          data-testid="content-about_certifications"
          className="w-full bg-transparent border border-border rounded-lg px-4 py-3 outline-none focus:border-foreground transition-colors resize-none font-mono text-sm"
        />
      </FieldRow>

      <FieldRow label="About — Education">
        <EducationEditor
          value={data.about_education || []}
          onChange={(next) => setData({ ...data, about_education: next })}
        />
      </FieldRow>

      <button onClick={save} disabled={saving} data-testid="content-save-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-foreground text-background font-mono text-xs uppercase tracking-[0.2em] disabled:opacity-50">
        <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save changes"}
      </button>
    </div>
  );
}

// ------------------------- COLLECTIONS (services / talks) -------------------------
function CollectionEditor({ kind, onSaved }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get(`/admin/${kind}`);
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, [kind]);

  const blank = kind === "services"
    ? { title: "", tagline: "", description: "", icon: "shield", order: (items.length || 0) + 1 }
    : { title: "", venue: "", date: "", summary: "", highlights: [], order: (items.length || 0) + 1 };

  const create = async () => {
    try {
      const { data } = await api.post(`/admin/${kind}`, { ...blank, id: crypto.randomUUID() });
      setItems([...items, data]);
      toast.success("Added.");
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const save = async (item) => {
    try {
      await api.put(`/admin/${kind}/${item.id}`, item);
      toast.success("Saved.");
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/admin/${kind}/${id}`);
    setItems(items.filter((i) => i.id !== id));
    onSaved && onSaved();
    toast.success("Deleted.");
  };

  if (loading) return <div className="font-mono text-xs text-muted-foreground">[ loading ]</div>;

  return (
    <div data-testid={`${kind}-editor`}>
      <div className="flex items-center justify-between mb-6">
        <SectionHeader title={kind === "services" ? "Services" : "Speaking"} desc={kind === "services" ? "Consulting service lines shown on Home & Services page." : "Talks, keynotes, and teaching series."} />
        <button onClick={create} data-testid={`${kind}-add-btn`} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border font-mono text-xs uppercase tracking-[0.2em] hover:bg-secondary">
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
      </div>
      <div className="space-y-6">
        {items.map((item, idx) => (
          <ItemCard key={item.id} item={item} kind={kind} onChange={(next) => {
            const copy = [...items]; copy[idx] = next; setItems(copy);
          }} onSave={save} onDelete={remove} idx={idx} />
        ))}
        {items.length === 0 && <div className="p-6 border border-dashed border-border rounded-lg text-muted-foreground text-sm">No items yet. Click Add.</div>}
      </div>
    </div>
  );
}

function ItemCard({ item, kind, onChange, onSave, onDelete, idx }) {
  const set = (k, v) => onChange({ ...item, [k]: v });

  return (
    <div className="p-6 border border-border rounded-xl bg-card space-y-4" data-testid={`${kind}-item-${idx}`}>
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">[ 0{idx + 1} ]</span>
        <div className="flex gap-2">
          <button onClick={() => onSave(item)} className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-full bg-foreground text-background">Save</button>
          <button onClick={() => onDelete(item.id)} className="text-xs font-mono uppercase tracking-wider p-1.5 rounded-full border border-border hover:bg-destructive hover:text-destructive-foreground transition-colors">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <TinyInput label="Title" value={item.title} onChange={(v) => set("title", v)} />
      {kind === "services" ? (
        <>
          <TinyInput label="Tagline" value={item.tagline} onChange={(v) => set("tagline", v)} />
          <TinyText label="Description" value={item.description} onChange={(v) => set("description", v)} />
          <TinyInput label="Icon (shield / graduation-cap / shield-check / target / mic)" value={item.icon} onChange={(v) => set("icon", v)} />
          <TinyInput label="Order" type="number" value={item.order} onChange={(v) => set("order", parseInt(v || 0))} />
        </>
      ) : (
        <>
          <TinyInput label="Venue" value={item.venue} onChange={(v) => set("venue", v)} />
          <TinyInput label="Date" value={item.date} onChange={(v) => set("date", v)} />
          <TinyText label="Summary" value={item.summary} onChange={(v) => set("summary", v)} />
          <TinyText label="Highlights (one per line)" value={(item.highlights || []).join("\n")} onChange={(v) => set("highlights", v.split("\n").filter(Boolean))} />
          <ImageUploader value={item.cover_image} onChange={(v) => set("cover_image", v)} testid={`${kind}-cover-uploader-${idx}`} />
          <TinyInput label="Order" type="number" value={item.order} onChange={(v) => set("order", parseInt(v || 0))} />
        </>
      )}
    </div>
  );
}

// ------------------------- POSTS -------------------------
function PostsEditor({ onSaved }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/admin/posts");
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ title: "", excerpt: "", body: "", tag: "Insight", published: true });

  const save = async () => {
    try {
      if (editing.id) {
        await api.put(`/admin/posts/${editing.id}`, editing);
      } else {
        await api.post("/admin/posts", editing);
      }
      toast.success("Saved.");
      setEditing(null);
      load();
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/admin/posts/${id}`);
    load();
    onSaved && onSaved();
  };

  if (loading) return <div className="font-mono text-xs text-muted-foreground">[ loading ]</div>;

  if (editing) {
    return (
      <div className="space-y-4" data-testid="post-editor-form">
        <SectionHeader title={editing.id ? "Edit post" : "New post"} desc="Markdown supported. Use the toolbar or type it by hand." />
        <TinyInput label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} />
        <TinyInput label="Tag" value={editing.tag} onChange={(v) => setEditing({ ...editing, tag: v })} />
        <TinyText label="Excerpt" value={editing.excerpt} onChange={(v) => setEditing({ ...editing, excerpt: v })} />
        <ImageUploader value={editing.cover_image} onChange={(v) => setEditing({ ...editing, cover_image: v })} testid="post-cover-uploader" />
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Body (Markdown)</label>
          <div className="mt-1.5">
            <MarkdownEditor
              value={editing.body}
              onChange={(v) => setEditing({ ...editing, body: v })}
              testid="post-body-editor"
            />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} data-testid="post-published-checkbox" />
          Published
        </label>
        <div className="flex gap-3">
          <button onClick={save} data-testid="post-save-btn" className="px-6 py-2.5 rounded-full bg-foreground text-background font-mono text-xs uppercase tracking-[0.2em]">Save</button>
          <button onClick={() => setEditing(null)} className="px-6 py-2.5 rounded-full border border-border font-mono text-xs uppercase tracking-[0.2em]">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="posts-editor">
      <div className="flex items-center justify-between mb-6">
        <SectionHeader title="Insights" desc="Blog posts and thought leadership." />
        <button onClick={startNew} data-testid="post-new-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border font-mono text-xs uppercase tracking-[0.2em] hover:bg-secondary">
          <Plus className="w-3.5 h-3.5" /> New post
        </button>
      </div>
      <div className="space-y-3">
        {items.map((p) => (
          <div key={p.id} className="flex items-center justify-between p-4 border border-border rounded-lg bg-card">
            <div>
              <div className="font-serif text-lg">{p.title}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">
                {p.tag} · {p.published ? "published" : "draft"}
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditing(p)} className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-full border border-border">Edit</button>
              <button onClick={() => remove(p.id)} className="text-xs font-mono uppercase tracking-wider p-1.5 rounded-full border border-border">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ------------------------- MESSAGES -------------------------
function MessagesView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/admin/messages");
      setItems(data);
      setLoading(false);
    })();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    await api.delete(`/admin/messages/${id}`);
    setItems(items.filter((i) => i.id !== id));
  };

  if (loading) return <div className="font-mono text-xs text-muted-foreground">[ loading ]</div>;

  return (
    <div data-testid="messages-view">
      <SectionHeader title="Inbox" desc="Contact form submissions." />
      {items.length === 0 && <div className="p-6 border border-dashed border-border rounded-lg text-muted-foreground text-sm mt-6">[ no messages yet ]</div>}
      <div className="mt-6 space-y-4">
        {items.map((m) => (
          <div key={m.id} className="p-6 border border-border rounded-xl bg-card">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-serif text-lg">{m.subject}</div>
                <div className="text-xs text-muted-foreground">
                  {m.name} · <a className="link-underline" href={`mailto:${m.email}`}>{m.email}</a>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  {new Date(m.created_at).toLocaleString()}
                </span>
                <button onClick={() => remove(m.id)} className="p-1.5 rounded-full border border-border">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <p className="text-sm whitespace-pre-wrap leading-relaxed">{m.message}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ------------------------- SIMPLE LIST (messages / bookings) -------------------------
function SimpleList({ kind, title, desc, renderItem }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get(`/admin/${kind}`);
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, [kind]);

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/admin/${kind}/${id}`);
    setItems(items.filter((i) => i.id !== id));
  };

  if (loading) return <div className="font-mono text-xs text-muted-foreground">[ loading ]</div>;

  return (
    <div data-testid={`${kind}-view`}>
      <SectionHeader title={title} desc={desc} />
      {items.length === 0 && <div className="p-6 border border-dashed border-border rounded-lg text-muted-foreground text-sm mt-6">[ no items yet ]</div>}
      <div className="mt-6 space-y-4">
        {items.map((m) => (
          <div key={m.id} className="p-6 border border-border rounded-xl bg-card relative">
            <button onClick={() => remove(m.id)} className="absolute top-4 right-4 p-1.5 rounded-full border border-border">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
            <div className="pr-10">{renderItem(m)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SubscribersView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/admin/subscribers");
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Remove this subscriber?")) return;
    await api.delete(`/admin/subscribers/${id}`);
    setItems(items.filter((i) => i.id !== id));
  };

  const exportCsv = () => {
    const csv = "email,subscribed_at\n" + items.map((i) => `${i.email},${i.created_at}`).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="font-mono text-xs text-muted-foreground">[ loading ]</div>;

  return (
    <div data-testid="subscribers-view">
      <div className="flex items-center justify-between">
        <SectionHeader title="Subscribers" desc={`${items.length} people waiting on your next Insight.`} />
        <button
          onClick={exportCsv}
          data-testid="subscribers-export-btn"
          disabled={items.length === 0}
          className="px-4 py-2 rounded-full border border-border font-mono text-xs uppercase tracking-[0.2em] disabled:opacity-50"
        >
          Export CSV
        </button>
      </div>
      {items.length === 0 && <div className="p-6 mt-6 border border-dashed border-border rounded-lg text-muted-foreground text-sm">[ no subscribers yet ]</div>}
      <div className="mt-6 space-y-2">
        {items.map((s) => (
          <div key={s.id} className="flex items-center justify-between px-5 py-3 border border-border rounded-lg bg-card">
            <div>
              <a href={`mailto:${s.email}`} className="link-underline">{s.email}</a>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-0.5">
                {new Date(s.created_at).toLocaleDateString()}
              </div>
            </div>
            <button onClick={() => remove(s.id)} className="p-1.5 rounded-full border border-border">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div className="p-3 rounded-lg bg-secondary/40">
      <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-muted-foreground">{label}</div>
      <div className="text-sm mt-1">{value}</div>
    </div>
  );
}

// ------------------------- SHARED PRIMITIVES -------------------------
function EducationEditor({ value, onChange }) {
  const rows = value || [];
  const update = (idx, key, v) => {
    const next = rows.map((r, i) => (i === idx ? { ...r, [key]: v } : r));
    onChange(next);
  };
  const add = () => onChange([...rows, { school: "", detail: "" }]);
  const remove = (idx) => onChange(rows.filter((_, i) => i !== idx));

  return (
    <div className="space-y-3" data-testid="education-editor">
      {rows.map((row, idx) => (
        <div key={idx} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-start">
          <input
            value={row.school || ""}
            onChange={(e) => update(idx, "school", e.target.value)}
            placeholder="School / Institution"
            data-testid={`education-school-${idx}`}
            className="bg-transparent border border-border rounded-lg px-3 py-2 outline-none focus:border-foreground transition-colors text-sm"
          />
          <input
            value={row.detail || ""}
            onChange={(e) => update(idx, "detail", e.target.value)}
            placeholder="Degree / detail"
            data-testid={`education-detail-${idx}`}
            className="bg-transparent border border-border rounded-lg px-3 py-2 outline-none focus:border-foreground transition-colors text-sm"
          />
          <button
            type="button"
            onClick={() => remove(idx)}
            data-testid={`education-remove-${idx}`}
            className="p-2 rounded-lg border border-border hover:bg-secondary transition-colors"
            aria-label="Remove"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        data-testid="education-add-btn"
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-border font-mono text-xs uppercase tracking-[0.2em] hover:bg-secondary transition-colors"
      >
        <Plus className="w-3.5 h-3.5" /> Add education
      </button>
    </div>
  );
}

function SectionHeader({ title, desc }) {
  return (
    <div>
      <h2 className="font-serif text-2xl tracking-tight">{title}</h2>
      <p className="text-sm text-muted-foreground mt-1">{desc}</p>
    </div>
  );
}
function FieldRow({ label, children }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
function TinyInput({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{label}</label>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-1.5 w-full bg-transparent border border-border rounded-lg px-4 py-2 outline-none focus:border-foreground transition-colors" />
    </div>
  );
}
function TinyText({ label, value, onChange, rows = 3 }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{label}</label>
      <textarea rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-1.5 w-full bg-transparent border border-border rounded-lg px-4 py-2 outline-none focus:border-foreground transition-colors resize-none" />
    </div>
  );
}
